// vg-binding: CONTEXT:decisions  (P999.11.D-09/D-14/D-15/D-16 tokens + D-04 dark-ok)
// apps/admin/src/pages/billing/InvoiceListPage.tsx
//
// Phase 8.2 Wave 3 Task 11 — Admin A1 Invoice list (cross-merchant +
// bulk resend email). Extends the Phase 4.1 Wave 13 baseline with the
// canonical Phase 8.2 contract (6-state status enum + bulk endpoint
// single-call + numbering prefix badge + immutability).
//
// vg-binding: PLAN:task-11
// vg-binding: P8.D-88
// vg-binding: P8.D-485
// vg-binding: P8.D-455
// vg-binding: BR-11
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: INTERFACE-STANDARDS:idempotency-key
//
// Per CONTEXT.md /.md §6.28:
//   • A1 — DataTable cross-merchant invoice list
//     filters (merchant, status, period, overdue flag), bulk "Resend
//     Email" action.
//   • Numbering prefix is colour-coded per doc type
//     (INV/MINV/CN/DN/PR).
//   • Invoices are IMMUTABLE: no edit, no delete. Only
//     "View" + bulk "Resend Email" actions are visible.
//   • `invoice:resend_email` permission gates the bulk
//     action (kế toán role default).
//   • Money column uses the shared `<Money>` component
//     (packages/ui — Task 05 USD canonical).
//
// Endpoints consumed (per API-CONTRACTS):
//   GET  /api/v1/admin/invoices
//          ?page=&limit=&merchant_id=&status[]=&period_from=&period_to=
//          &overdue_only=&q=&sort=
//   POST /api/v1/admin/invoices/resend-email
//          { invoice_ids: string[], cc_emails?: string[] }
//          Headers: Idempotency-Key (required, 422 otherwise)
//
// URL state (VG R7 list-view convention):
//   ?page, ?status, ?merchant_id, ?period_from, ?period_to,
//   ?overdue_only, ?q
//
// Error envelope handling per INTERFACE-STANDARDS:
//   Toast description = `error.user_message || error.message || fallback`.
//   NEVER show AxiosError.message / Response.statusText to the user —
//   `apiClient` already throws `ApiError` carrying the resolved copy
//   and `listAdminInvoices` re-throws same.

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  Button,
  Card,
  CardContent,
  Money,
  cn,
  useToast,
  PageHeader,
} from '@printway/ui';
import { PageListFooter } from '../../components/datatable/PageListFooter.js';

import { useTranslation } from '../../i18n/useTranslation.js';
import { ApiError } from '../../lib/apiClient.js';
import { billingStatusTone } from '../../components/billing/BillingStatusBadge.js';
import { StatusBadge } from '../../components/ui/StatusBadge.js';
import { NETWORK_FALLBACK_MESSAGE } from '../../lib/errorHandler.js';
import { PERMISSIONS } from '../../lib/permissions.js';
import { useAuthStore } from '../../stores/useAuthStore.js';
import {
  bulkResendInvoiceEmail,
  listAdminInvoices,
} from '../../api/finance.api.js';
import { financeKeys } from '../../api/queries/financeKeys.js';
import type {
  AdminInvoiceListFilters,
  AdminInvoiceRow,
  AdminInvoiceStatus,
  BulkResendInvoiceEmailResponse,
} from '../../types/finance.js';
import {
  InvoiceFilters,
  type InvoiceStatusFilterValue,
} from '../../features/billing/InvoiceFilters.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Phase 8.2 audit 2026-05-23 — DEFAULT_PAGE_SIZE bumped 20 → 25 to match the
// canonical /stores PageListFooter pageSizeChoices [10, 25, 50, 100]. Live
// value flows from URL-synced `pageSize` searchParam (clamped 10..100).
const DEFAULT_PAGE_SIZE = 25;

/**
 * invoice numbering format:
 *   `INV-YYYYMM-XXXXX`   auto-generated
 *   `MINV-YYYYMM-XXXXX`  manual
 *   `CN-YYYYMM-XXXXX`    credit note
 *   `DN-YYYYMM-XXXXX`    debit note
 *   `PR-YYYYMM-XXXXX`    pre-payment request
 *
 * Each prefix gets a distinct colour so admins can scan the list and
 * separate auto-billing from manual / credit / debit / pre-payment
 * artefacts without reading every cell.
 */
const NUMBER_PREFIX_STYLES: Record<
  'INV' | 'MINV' | 'CN' | 'DN' | 'PR' | 'OTHER',
  { label: string; cls: string }
> = {
  INV: {
    label: 'INV',
    cls: 'bg-blue-100 dark:bg-blue-950/40 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  },
  MINV: {
    label: 'MINV',
    cls: 'bg-indigo-100 dark:bg-indigo-950/40 text-indigo-800 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800',
  },
  CN: {
    label: 'CN',
    cls: 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
  },
  DN: {
    label: 'DN',
    cls: 'bg-orange-100 dark:bg-orange-950/40 text-orange-800 dark:text-orange-300 border-orange-200 dark:border-orange-800',
  },
  PR: {
    label: 'PR',
    cls: 'bg-purple-100 dark:bg-purple-950/40 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-800',
  },
  OTHER: {
    label: '—',
    cls: 'bg-pw-surface text-pw-text border-pw-border',
  },
};

/** Statuses that are eligible for bulk resend (i.e. an email already
 * exists in the merchant's inbox or is ready to send). DRAFT and VOID
 * are filtered out client-side AND server-side (BE returns them in the
 * `skipped[]` array if forced). */
const RESEND_ELIGIBLE_STATUSES: ReadonlySet<AdminInvoiceStatus> = new Set([
  'SENT',
  'PARTIAL',
  'OVERDUE',
  'PAID',
]);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function classifyPrefix(
  invoiceNumber: string,
): keyof typeof NUMBER_PREFIX_STYLES {
  // Extract prefix up to the first dash. Defensive against payloads that
  // omit the prefix (legacy Phase 3 numbers) — falls back to OTHER.
  const dashIdx = invoiceNumber.indexOf('-');
  const prefix = (dashIdx > 0 ? invoiceNumber.slice(0, dashIdx) : invoiceNumber)
    .toUpperCase();
  if (
    prefix === 'INV' ||
    prefix === 'MINV' ||
    prefix === 'CN' ||
    prefix === 'DN' ||
    prefix === 'PR'
  ) {
    return prefix;
  }
  return 'OTHER';
}

function extractErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    // ApiError already follows the INTERFACE-STANDARDS message-priority
    // (user_message > message > fallback) — its `.message` is safe.
    return err.message || NETWORK_FALLBACK_MESSAGE;
  }
  if (err instanceof Error && err.message) return err.message;
  return NETWORK_FALLBACK_MESSAGE;
}

// ---------------------------------------------------------------------------
// Status badge — 6 distinct colours per acceptance criteria
// ---------------------------------------------------------------------------

// Phase 8.x Debug B3 — local badge replaced by canonical `<StatusBadge>` so
// the visual language matches every other status pill in the admin app.
// `PARTIAL` keeps its bespoke cyan label by mapping through `info` tone.
const KNOWN_INVOICE_STATUSES = new Set<string>([
  'DRAFT',
  'SENT',
  'PARTIAL',
  'PAID',
  'OVERDUE',
  'VOID',
]);

function InvoiceStatusBadge({
  status,
}: {
  status: AdminInvoiceStatus;
}): JSX.Element {
  const { t } = useTranslation('finance');
  const label = KNOWN_INVOICE_STATUSES.has(status)
    ? t(`invoice.statusLabels.${status}`)
    : String(status);

  // PARTIAL uses the `info` tone (cyan-ish) rather than the generic mapper.
  const tone =
    status === 'PARTIAL' ? 'info'
    : status === 'DRAFT' ? 'inactive'
    : status === 'SENT' ? 'pending'
    : status === 'PAID' ? 'approved'
    : status === 'OVERDUE' ? 'rejected'
    : status === 'VOID' ? 'closed'
    : billingStatusTone(status);

  return (
    <StatusBadge
      tone={tone}
      label={label}
      className={`status-invoice-${String(status).toLowerCase()}`}
      title={undefined}
    />
  );
}

// dbg-admin-invoices-cols-2026-05-23 — three-tone Type pill rendered next to
// the invoice number. Refund = rose, Surcharge = amber, Payment = neutral.
function InvoiceTypeBadge({
  value,
}: {
  value: 'Payment' | 'Refund' | 'Surcharge';
}): JSX.Element {
  const { t } = useTranslation('finance');
  const cls =
    value === 'Refund'
      ? 'bg-rose-100 dark:bg-rose-950/40 text-rose-800 dark:text-rose-300 border-rose-200 dark:border-rose-800'
      : value === 'Surcharge'
        ? 'bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800'
        : 'bg-pw-surface text-pw-text border-pw-border';
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium',
        cls,
      )}
      data-testid={`invoice-type-${value.toLowerCase()}`}
    >
      {t(`invoice.typeLabels.${value}`)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Numbering prefix badge — colour-coded
// ---------------------------------------------------------------------------

function NumberingPrefixBadge({
  invoiceNumber,
}: {
  invoiceNumber: string;
}): JSX.Element {
  const { t } = useTranslation('finance');
  const kind = classifyPrefix(invoiceNumber);
  const style = NUMBER_PREFIX_STYLES[kind];
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide',
        style.cls,
      )}
      data-testid={`invoice-prefix-${kind}`}
      title={t('invoice.list.prefixTooltip', { prefix: style.label })}
    >
      {style.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function InvoiceListPage(): JSX.Element {
  const { toast } = useToast();
  const { t } = useTranslation('finance');
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const canResendEmail = useAuthStore((s) =>
    s.hasPermission(PERMISSIONS.INVOICE_RESEND_EMAIL),
  );

  // ---- URL state ----
  const page = Math.max(
    1,
    Number.parseInt(searchParams.get('page') ?? '1', 10) || 1,
  );
  // Phase 8.2 audit 2026-05-23 — pageSize is URL-synced per /stores standard,
  // clamped to the allowlist so a hand-typed value can't break the BE limit cap.
  const rawPageSize = Number.parseInt(
    searchParams.get('pageSize') ?? String(DEFAULT_PAGE_SIZE),
    10,
  );
  const pageSize = Number.isFinite(rawPageSize) && rawPageSize > 0
    ? Math.min(100, Math.max(10, rawPageSize))
    : DEFAULT_PAGE_SIZE;
  // status URL param may be:
  //   • empty: no filter
  //   • single value: 'PAID'
  //   • CSV: 'SENT,OVERDUE' (AgingReportPage drill-down emits this)
  // We surface a single value to the filter dropdown UI (first token);
  // the full set is forwarded to the API call as a multi-status filter
  // below via `f.status = statuses`.
  const statusRaw = searchParams.get('status') ?? '';
  const statuses = useMemo(
    () =>
      statusRaw
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0) as AdminInvoiceStatus[],
    [statusRaw],
  );
  const status = (statuses[0] ?? '') as InvoiceStatusFilterValue;
  const merchantId = searchParams.get('merchant_id') ?? '';
  const periodFrom = searchParams.get('period_from') ?? '';
  const periodTo = searchParams.get('period_to') ?? '';
  const overdueOnly = searchParams.get('overdue_only') === 'true';
  const search = searchParams.get('q') ?? '';

  // ---- Local UI state ----
  const [searchDraft, setSearchDraft] = useState(search);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Keep search input synced with external URL changes (back/forward).
  useEffect(() => {
    setSearchDraft(search);
  }, [search]);

  // ---- URL helpers ----
  const updateParam = useCallback(
    (key: string, value: string): void => {
      const next = new URLSearchParams(searchParams);
      if (value === '' || value === undefined) next.delete(key);
      else next.set(key, value);
      // Any filter change resets page to 1 to avoid landing on empty pages.
      if (key !== 'page') next.delete('page');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setPage = useCallback(
    (n: number): void => {
      const next = new URLSearchParams(searchParams);
      if (n <= 1) next.delete('page');
      else next.set('page', String(n));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const clearFilters = useCallback((): void => {
    setSearchParams(new URLSearchParams(), { replace: true });
    setSearchDraft('');
  }, [setSearchParams]);

  // ---- Build filter object for the query key + API call ----
  const filters: AdminInvoiceListFilters = useMemo(() => {
    const f: AdminInvoiceListFilters = {
      page,
      limit: pageSize,
    };
    if (statuses.length > 0) f.status = statuses;
    if (merchantId) f.merchant_id = merchantId;
    if (periodFrom) f.period_from = periodFrom;
    if (periodTo) f.period_to = periodTo;
    if (overdueOnly) f.overdue_only = true;
    if (search) f.q = search;
    return f;
  }, [page, pageSize, statuses, merchantId, periodFrom, periodTo, overdueOnly, search]);

  // ---- List query ----
  const listQuery = useQuery({
    queryKey: financeKeys.invoices.list(filters),
    queryFn: () => listAdminInvoices(filters),
    placeholderData: (prev) => prev,
  });

  // Surface fetch errors via toast (effect — keeps the query body pure).
  useEffect(() => {
    if (listQuery.isError) {
      toast({
        title: t('invoice.errors.loadFailed'),
        description: extractErrorMessage(listQuery.error),
        variant: 'error',
      });
    }
  }, [listQuery.isError, listQuery.error, toast, t]);

  const listRows = listQuery.data?.rows;
  const rows: AdminInvoiceRow[] = useMemo(() => listRows ?? [], [listRows]);
  const meta = listQuery.data?.meta;
  const totalPages = meta && meta.limit > 0
    ? Math.max(1, Math.ceil(meta.total / meta.limit))
    : 1;

  // ---- Selection helpers ----
  const allOnPageSelected =
    rows.length > 0 && rows.every((r) => selected.has(r.id));

  function toggleAllOnPage(): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) rows.forEach((r) => next.delete(r.id));
      else rows.forEach((r) => next.add(r.id));
      return next;
    });
  }

  function toggleOne(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function clearSelection(): void {
    setSelected(new Set());
  }

  // Eligible subset of the current selection — drives the bulk button
  // label + disabled state so the user sees "0 eligible" before clicking.
  const eligibleSelectedIds = useMemo<string[]>(() => {
    if (selected.size === 0) return [];
    const byId = new Map(rows.map((r) => [r.id, r] as const));
    const out: string[] = [];
    for (const id of selected) {
      const row = byId.get(id);
      if (row && RESEND_ELIGIBLE_STATUSES.has(row.status)) out.push(id);
    }
    return out;
  }, [selected, rows]);

  // ---- Bulk resend mutation ----
  const bulkResend = useMutation({
    mutationFn: async (
      invoiceIds: string[],
    ): Promise<BulkResendInvoiceEmailResponse> =>
      bulkResendInvoiceEmail({ invoice_ids: invoiceIds }),
    onSuccess: (data, invoiceIds) => {
      const totalRequested = invoiceIds.length;
      const queued = data.queued_count ?? 0;
      const skipped = data.skipped ?? [];
      if (queued > 0 && skipped.length === 0) {
        toast({
          title:
            queued === 1
              ? t('invoice.list.toast.oneQueued')
              : t('invoice.list.toast.manyQueued', { count: queued }),
          description: t('invoice.list.toast.queuedDesc'),
          variant: 'success',
        });
      } else if (queued > 0 && skipped.length > 0) {
        const firstReason = skipped[0]?.reason ?? 'ineligible';
        toast({
          title: t('invoice.list.toast.partialQueued', {
            queued,
            total: totalRequested,
          }),
          description: t('invoice.list.toast.partialDesc', {
            count: skipped.length,
            reason: firstReason,
          }),
          variant: 'warning',
        });
      } else {
        toast({
          title: t('invoice.list.toast.skippedTitle'),
          description:
            skipped[0]?.reason ?? t('invoice.list.toast.skippedDescFallback'),
          variant: 'error',
        });
      }
      clearSelection();
      // Re-fetch the list so overdue_count / outstanding totals refresh.
      void queryClient.invalidateQueries({
        queryKey: financeKeys.invoices.all,
      });
    },
    onError: (err) => {
      toast({
        title: t('invoice.list.toast.resendFailed'),
        description: extractErrorMessage(err),
        variant: 'error',
      });
    },
  });

  function handleBulkResend(): void {
    if (eligibleSelectedIds.length === 0) return;
    bulkResend.mutate(eligibleSelectedIds);
  }

  // ---- Render ----
  const hasAnyFilter = !!(
    status ||
    merchantId ||
    periodFrom ||
    periodTo ||
    overdueOnly ||
    search
  );

  return (
    <div className="flex flex-col gap-6 p-6" data-testid="admin-invoices-page">
      {/* Page header --------------------------------------------------- */}
      <PageHeader
        title={t('invoice.title')}
        subtitle={t('invoice.list.subtitle')}
        actions={
          <Link
            to="/billing/invoices/manual-generate"
            className="inline-flex h-11 items-center justify-center rounded-lg bg-pw-primary px-5 text-sm font-medium text-white transition-colors hover:bg-pw-primary-hover"
            data-testid="admin-invoices-manual-generate-link"
          >
            {t('invoice.list.generateManual')}
          </Link>
        }
      />

      {/* Filters -------------------------------------------------------- */}
      <InvoiceFilters
        search={search}
        searchDraft={searchDraft}
        onSearchDraftChange={setSearchDraft}
        onSearchCommit={(next) => updateParam('q', next)}
        status={status}
        onStatusChange={(next) => updateParam('status', next)}
        merchantId={merchantId}
        onMerchantChange={(id) => updateParam('merchant_id', id)}
        periodFrom={periodFrom}
        periodTo={periodTo}
        onPeriodFromChange={(iso) => updateParam('period_from', iso)}
        onPeriodToChange={(iso) => updateParam('period_to', iso)}
        overdueOnly={overdueOnly}
        onOverdueOnlyChange={(next) =>
          updateParam('overdue_only', next ? 'true' : '')
        }
        hasAnyFilter={hasAnyFilter}
        onClearAll={clearFilters}
        overdueCount={meta?.overdue_count}
      />

      {/* Bulk-action bar (≥1 selected) ---------------------------------- */}
      {selected.size > 0 && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-pw-primary/30 bg-pw-primary/5 px-4 py-3"
          data-testid="invoices-bulk-action-bar"
        >
          <div className="text-sm font-medium text-pw-text">
            {t('invoice.list.bulk.selected', { count: selected.size })}
            {eligibleSelectedIds.length !== selected.size ? (
              <span className="ml-2 text-xs text-pw-text-muted">
                {t('invoice.list.bulk.eligibleNote', {
                  count: eligibleSelectedIds.length,
                })}
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="default"
              size="sm"
              data-testid="invoices-bulk-resend-btn"
              onClick={handleBulkResend}
              disabled={
                !canResendEmail ||
                eligibleSelectedIds.length === 0 ||
                bulkResend.isPending
              }
              title={
                !canResendEmail
                  ? t('invoice.list.bulk.noPermission')
                  : eligibleSelectedIds.length === 0
                    ? t('invoice.list.bulk.selectEligible')
                    : undefined
              }
            >
              {bulkResend.isPending
                ? t('invoice.list.bulk.sending')
                : t('invoice.list.bulk.resend', {
                    count: eligibleSelectedIds.length,
                  })}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSelection}
              data-testid="invoices-bulk-clear-btn"
            >
              {t('invoice.list.bulk.clear')}
            </Button>
          </div>
        </div>
      )}

      {/* Table card ----------------------------------------------------- */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table
              className="w-full text-sm"
              data-testid="admin-invoices-table"
            >
              <thead className="border-b border-pw-border bg-pw-surface/60 text-left text-xs font-medium uppercase tracking-wide text-pw-text-muted">
                <tr>
                  <th className="w-[44px] px-4 py-3">
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={toggleAllOnPage}
                      aria-label={t('invoice.list.selectAllAria')}
                      className="h-4 w-4 rounded border-pw-border accent-pw-primary"
                      data-testid="invoices-select-all"
                    />
                  </th>
                  <th className="px-4 py-3">{t('invoice.list.columns.invoiceNumber')}</th>
                  <th className="px-4 py-3">{t('invoice.list.columns.type')}</th>
                  <th className="px-4 py-3">{t('invoice.list.columns.merchant')}</th>
                  <th className="px-4 py-3">{t('invoice.list.columns.period')}</th>
                  <th className="px-4 py-3 text-right">{t('invoice.list.columns.amount')}</th>
                  <th className="px-4 py-3">{t('invoice.list.columns.paymentGateway')}</th>
                  <th className="px-4 py-3 text-right">{t('invoice.list.columns.gatewayFee')}</th>
                  <th className="px-4 py-3">{t('invoice.list.columns.status')}</th>
                  <th className="px-4 py-3">{t('invoice.list.columns.due')}</th>
                  <th className="w-[80px] px-4 py-3 text-right">{t('invoice.list.columns.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {listQuery.isLoading ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-4 py-12 text-center text-pw-text-muted"
                    >
                      {t('invoice.list.loading')}
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-4 py-12 text-center text-pw-text-muted"
                    >
                      {hasAnyFilter
                        ? t('invoice.list.emptyFiltered')
                        : t('invoice.empty.title')}
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => {
                    const isSelected = selected.has(row.id);
                    return (
                      <tr
                        key={row.id}
                        data-testid={`invoices-row-${row.id}`}
                        className={cn(
                          'border-b border-pw-border last:border-b-0 transition-colors hover:bg-pw-surface/50',
                          isSelected && 'bg-pw-primary/5',
                        )}
                      >
                        <td className="px-4 py-4 align-middle">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleOne(row.id)}
                            aria-label={t('invoice.list.selectRowAria', {
                              number: row.invoice_number,
                            })}
                            className="h-4 w-4 rounded border-pw-border accent-pw-primary"
                            data-testid={`invoices-row-select-${row.id}`}
                          />
                        </td>
                        <td className="px-4 py-4 align-middle">
                          <div className="flex items-center gap-2">
                            <NumberingPrefixBadge
                              invoiceNumber={row.invoice_number}
                            />
                            <span className="font-mono text-xs text-pw-text">
                              {row.invoice_number}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 align-middle">
                          <InvoiceTypeBadge value={row.invoice_type ?? 'Payment'} />
                        </td>
                        <td className="px-4 py-4 align-middle">
                          <div className="font-medium text-pw-text">
                            {row.merchant_name}
                          </div>
                          <div
                            className="truncate text-xs text-pw-text-muted"
                            title={row.merchant_email ?? row.merchant_id}
                          >
                            {row.merchant_email ?? `${row.merchant_id.slice(0, 8)}…`}
                          </div>
                        </td>
                        <td className="px-4 py-4 align-middle text-pw-text-muted">
                          {formatDate(row.period_from)} →{' '}
                          {formatDate(row.period_to)}
                        </td>
                        <td className="px-4 py-4 align-middle text-right">
                          <Money usd={row.total_cents} align="right" />
                        </td>
                        <td className="px-4 py-4 align-middle">
                          {row.payment_gateway ? (
                            <span className="inline-flex items-center rounded-md border border-pw-border bg-pw-surface px-2 py-0.5 font-mono text-xs text-pw-text">
                              {row.payment_gateway}
                            </span>
                          ) : (
                            <span className="text-pw-text-muted">—</span>
                          )}
                        </td>
                        <td className="px-4 py-4 align-middle text-right">
                          {row.payment_gateway_fee_cents != null ? (
                            <Money usd={row.payment_gateway_fee_cents} align="right" />
                          ) : (
                            <span className="text-pw-text-muted">—</span>
                          )}
                        </td>
                        <td className="px-4 py-4 align-middle">
                          <InvoiceStatusBadge status={row.status} />
                        </td>
                        <td className="px-4 py-4 align-middle text-pw-text">
                          {formatDate(row.due_at)}
                        </td>
                        <td className="px-4 py-4 align-middle text-right">
                          {/*  immutable — only View is offered.
                              No edit / no delete buttons. */}
                          <Link
                            to={`/billing/invoices/${row.id}`}
                            className="inline-flex h-8 items-center rounded-md border border-pw-border px-2.5 text-xs font-medium text-pw-text-muted hover:bg-pw-surface hover:text-pw-text"
                            data-testid={`invoices-row-view-${row.id}`}
                          >
                            {t('invoice.actions.view')}
                          </Link>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination — canonical /stores footer (Rows-per-page + Total +
              numbered) per audit 2026-05-23 Cluster A. */}
          <div className="border-t border-pw-border px-6 py-3">
            <PageListFooter
              page={meta?.page ?? page}
              pageSize={pageSize}
              total={meta?.total ?? 0}
              totalPages={totalPages}
              loading={listQuery.isFetching}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                const next = new URLSearchParams(searchParams);
                next.set('pageSize', String(size));
                next.delete('page');
                setSearchParams(next, { replace: true });
              }}
              testId="invoice-footer"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default InvoiceListPage;
